import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Cake, Loader2, Download, FileText, Archive, AlertTriangle, RefreshCw, EyeOff, Eye,
} from 'lucide-react';
import { desenharFlyer, canvasEmA4, FLYER } from './flyer-desenho';
import { MESES, tituloDeNome } from './flyer-texto';
import {
    ANIVERSARIANTES_FUNDO, ANIVERSARIANTES_TITULO,
    carregarFontes, carregarImagem, baixarArquivo,
} from '../marketing-recursos';

// =====================================================================
// Marketing › ANIVERSARIANTES DO MÊS — uma arte A4 por filial.
//
// ⚠️⚠️ ESTA TELA NÃO PEDE PLANILHA, e essa é a diferença que justifica a
//    reescrita. A ferramenta que rodava fora do portal começava por "envie a
//    planilha" e "confira as colunas" — dois passos que existiam só porque ela
//    não alcançava o banco. Aqui a lista sai da `dtbConfere` (a mesma do
//    cadastro de folha), e com os dois passos morreu a classe de erro que mais
//    doía: planilha velha, coluna trocada, gente que já saiu da empresa na arte.
//
// ⚠️ O QUE SOBROU DE AJUSTE MANUAL É DE PROPÓSITO: dá para OCULTAR alguém (quem
//    pediu para não aparecer) e CORRIGIR o cargo (o cadastro às vezes tem o
//    cargo formal, e a arte quer o que a equipe chama). São ajustes da ARTE, não
//    do cadastro — nada disso volta para o banco.
//
// ⚠️ O gerente de negócios aparece na arte da loja E na do Escritório; quem
//    decide isso é o backend (`marketing-aniversariantes.js`), não esta tela.
// =====================================================================

const API = '/api/marketing';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const COR = '#db2777';
const COR_ESCURA = '#9d174d';
const COR_CLARA = '#fdf2f8';
const COR_BORDA = '#fce7f3';

const botao = (primario) => ({
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '9px 16px', borderRadius: 10, fontSize: 14, fontWeight: 700,
    cursor: 'pointer', transition: 'all .2s', whiteSpace: 'nowrap',
    border: primario ? 'none' : `1.5px solid ${COR_BORDA}`,
    background: primario ? `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)` : '#fff',
    color: primario ? '#fff' : COR_ESCURA,
});

/** Nome do arquivo de uma filial: aniversariantes-anv-setembro.png */
const nomeBase = (sigla, mes) =>
    `aniversariantes-${String(sigla).toLowerCase()}-${MESES[mes - 1].toLowerCase()}`;

// ---------------------------------------------------------------------
// O cartão de uma filial: a arte desenhada + os ajustes + os downloads.
// O <canvas> tem sempre 1200×850 (o tamanho de projeto) e é só ENCOLHIDO por
// CSS na tela — o que se vê é exatamente o que sai no arquivo.
// ---------------------------------------------------------------------
const CartaoFilial = ({ filial, mes, imagens, aoRegistrarCanvas }) => {
    const canvasRef = useRef(null);
    const [ocultos, setOcultos] = useState(() => new Set());
    const [cargos, setCargos] = useState({});   // índice -> cargo corrigido
    const [abrirAjustes, setAbrirAjustes] = useState(false);

    const visiveis = useMemo(() => filial.pessoas
        .map((p, i) => ({ ...p, cargo: cargos[i] !== undefined ? cargos[i] : p.cargo, _i: i }))
        .filter((p) => !ocultos.has(p._i)), [filial.pessoas, cargos, ocultos]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        desenharFlyer(canvas.getContext('2d'), { pessoas: visiveis, mes, imagens });
        aoRegistrarCanvas(filial.sigla, canvas);
    }, [visiveis, mes, imagens, filial.sigla, aoRegistrarCanvas]);

    const baixarPng = () => {
        canvasEmA4(canvasRef.current).toBlob((blob) => {
            baixarArquivo(`${nomeBase(filial.sigla, mes)}.png`, blob);
        }, 'image/png');
    };

    const baixarPdf = async () => {
        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        pdf.addImage(canvasEmA4(canvasRef.current).toDataURL('image/png'), 'PNG', 0, 0,
            pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());
        baixarArquivo(`${nomeBase(filial.sigla, mes)}.pdf`, pdf.output('blob'));
    };

    return (
        <div style={{
            background: '#fff', border: `1px solid ${COR_BORDA}`, borderRadius: 16,
            padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span style={{
                        padding: '4px 10px', borderRadius: 999, background: COR_CLARA,
                        color: COR_ESCURA, fontWeight: 800, fontSize: 13, letterSpacing: '.04em',
                    }}>{filial.label}</span>
                    <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                        {visiveis.length} aniversariante{visiveis.length !== 1 ? 's' : ''}
                        {ocultos.size > 0 && ` · ${ocultos.size} oculto${ocultos.size !== 1 ? 's' : ''}`}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={baixarPng} style={{ ...botao(false), padding: '7px 12px', fontSize: 13 }}>
                        <Download size={15} /> PNG
                    </button>
                    <button onClick={baixarPdf} style={{ ...botao(false), padding: '7px 12px', fontSize: 13 }}>
                        <FileText size={15} /> PDF
                    </button>
                </div>
            </div>

            <canvas
                ref={canvasRef}
                width={FLYER.W}
                height={FLYER.H}
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 10, background: '#fff' }}
            />

            <button
                onClick={() => setAbrirAjustes((v) => !v)}
                style={{
                    ...botao(false), alignSelf: 'flex-start', padding: '6px 12px',
                    fontSize: 12.5, fontWeight: 600, color: '#64748b', borderColor: '#e2e8f0',
                }}
            >
                {abrirAjustes ? <Eye size={14} /> : <EyeOff size={14} />}
                Ocultar alguém ou corrigir cargo
            </button>

            {abrirAjustes && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {filial.pessoas.map((p, i) => (
                        <div key={`${p.nome}-${i}`} style={{
                            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                            padding: '6px 8px', borderRadius: 8,
                            background: ocultos.has(i) ? '#f8fafc' : 'transparent',
                        }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 240px', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={!ocultos.has(i)}
                                    onChange={(e) => setOcultos((antes) => {
                                        const novo = new Set(antes);
                                        if (e.target.checked) novo.delete(i); else novo.add(i);
                                        return novo;
                                    })}
                                    style={{ width: 16, height: 16, accentColor: COR }}
                                />
                                <span style={{
                                    fontSize: 13.5, color: ocultos.has(i) ? '#94a3b8' : '#0f172a',
                                    textDecoration: ocultos.has(i) ? 'line-through' : 'none',
                                }}>
                                    {tituloDeNome(p.nome)} ({String(p.dia).padStart(2, '0')}/{String(mes).padStart(2, '0')})
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder="Cargo"
                                value={cargos[i] !== undefined ? cargos[i] : (p.cargo || '')}
                                onChange={(e) => setCargos((antes) => ({ ...antes, [i]: e.target.value }))}
                                style={{
                                    flex: '1 1 180px', padding: '6px 10px', fontSize: 13,
                                    border: '1.5px solid #e2e8f0', borderRadius: 8, outline: 'none',
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------
// A tela
// ---------------------------------------------------------------------
const AniversariantesMes = () => {
    const [mes, setMes] = useState(new Date().getMonth() + 1);
    const [dados, setDados] = useState(null);
    const [imagens, setImagens] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [ocupado, setOcupado] = useState('');

    // Os canvases vivos, por filial — é deles que saem o ZIP e o PDF único.
    // ⚠️ `useRef` e não estado: guardar elemento de DOM em `useState` dispararia
    //    um render a cada arte desenhada, e o desenho acontece dentro do render.
    const canvases = useRef(new Map());
    const registrarCanvas = useCallback((sigla, canvas) => {
        canvases.current.set(sigla, canvas);
    }, []);

    // As artes e as fontes: uma vez só, na abertura da tela.
    useEffect(() => {
        let vivo = true;
        (async () => {
            await carregarFontes(['700 19px Fredoka', '600 34px Fredoka', '700 20px Poppins', '500 12px Poppins']);
            try {
                const [fundo, titulo] = await Promise.all([
                    carregarImagem(ANIVERSARIANTES_FUNDO),
                    carregarImagem(ANIVERSARIANTES_TITULO),
                ]);
                if (vivo) setImagens({ fundo, titulo });
            } catch (e) {
                if (vivo) setErro(e.message);
            }
        })();
        return () => { vivo = false; };
    }, []);

    const buscar = useCallback(async (mesAlvo) => {
        setCarregando(true);
        setErro('');
        canvases.current.clear();
        try {
            const r = await fetch(`${API}/aniversariantes?mes=${mesAlvo}`, { headers: auth() });
            if (!r.ok) throw new Error('Não foi possível buscar os aniversariantes.');
            setDados(await r.json());
        } catch (e) {
            setErro(e.message);
            setDados(null);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { buscar(mes); }, [mes, buscar]);

    const pngDaFilial = (sigla) => new Promise((resolve) => {
        canvasEmA4(canvases.current.get(sigla)).toBlob(resolve, 'image/png');
    });

    const baixarZip = async () => {
        setOcupado('zip');
        try {
            const JSZip = (await import('jszip')).default;
            const zip = new JSZip();
            for (const f of dados.filiais) {
                zip.file(`${nomeBase(f.sigla, mes)}.png`, await pngDaFilial(f.sigla));
            }
            baixarArquivo(
                `aniversariantes-${MESES[mes - 1].toLowerCase()}.zip`,
                await zip.generateAsync({ type: 'blob' }),
            );
        } finally {
            setOcupado('');
        }
    };

    const baixarPdfUnico = async () => {
        setOcupado('pdf');
        try {
            const { jsPDF } = await import('jspdf');
            let pdf = null;
            for (const f of dados.filiais) {
                if (!pdf) pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
                else pdf.addPage('a4', 'landscape');
                pdf.addImage(canvasEmA4(canvases.current.get(f.sigla)).toDataURL('image/png'), 'PNG', 0, 0,
                    pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());
            }
            if (pdf) {
                baixarArquivo(
                    `aniversariantes-${MESES[mes - 1].toLowerCase()}-todas-as-filiais.pdf`,
                    pdf.output('blob'),
                );
            }
        } finally {
            setOcupado('');
        }
    };

    const totalPessoas = (dados?.filiais || []).reduce((s, f) => s + f.pessoas.length, 0);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                <div style={{ padding: 10, borderRadius: 12, background: COR_CLARA, color: COR }}>
                    <Cake size={22} />
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                        Aniversariantes do Mês
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13.5, color: '#64748b' }}>
                        Uma arte A4 por filial, montada com quem está ativo no cadastro — sem planilha.
                    </p>
                </div>
            </div>

            <div style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                background: COR_CLARA, border: `1px solid ${COR_BORDA}`,
                borderRadius: 12, padding: 12, marginBottom: 24,
            }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: COR_ESCURA }}>Mês</label>
                <select
                    value={mes}
                    onChange={(e) => setMes(Number(e.target.value))}
                    style={{
                        padding: '8px 12px', borderRadius: 10, fontSize: 14, fontWeight: 600,
                        border: `1.5px solid ${COR_BORDA}`, background: '#fff', color: '#0f172a', outline: 'none',
                    }}
                >
                    {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>

                <button onClick={() => buscar(mes)} style={botao(false)}>
                    <RefreshCw size={15} /> Atualizar
                </button>

                <div style={{ flex: 1 }} />

                {!!dados?.filiais?.length && (
                    <>
                        <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                            {dados.filiais.length} filial(is) · {totalPessoas} pessoa(s)
                        </span>
                        <button onClick={baixarZip} disabled={!!ocupado} style={botao(true)}>
                            {ocupado === 'zip' ? <Loader2 size={15} className="girando" /> : <Archive size={15} />}
                            Baixar tudo (ZIP)
                        </button>
                        <button onClick={baixarPdfUnico} disabled={!!ocupado} style={botao(false)}>
                            {ocupado === 'pdf' ? <Loader2 size={15} /> : <FileText size={15} />}
                            PDF com todas
                        </button>
                    </>
                )}
            </div>

            {/* ⚠️ Quem ficou de fora aparece na tela. Foi exatamente assim que
                Linhares sumia da ferramenta antiga: o descarte não tinha voz. */}
            {!!dados?.descartados?.length && (
                <div style={{
                    display: 'flex', gap: 10, alignItems: 'flex-start',
                    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10,
                    padding: '10px 12px', marginBottom: 20, fontSize: 13, color: '#92400e',
                }}>
                    <AlertTriangle size={16} style={{ flex: 'none', marginTop: 2 }} />
                    <span>
                        <strong>Fora das artes:</strong>{' '}
                        {dados.descartados.map((d) => `${d.loja} (${d.quantidade})`).join(' · ')} —
                        são códigos de lotação que não são filial do grupo (prestadoras de serviço).
                    </span>
                </div>
            )}

            {erro && (
                <div style={{
                    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
                    padding: '12px 14px', color: '#991b1b', fontSize: 14, marginBottom: 20,
                }}>{erro}</div>
            )}

            {carregando && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 40 }}>
                    <Loader2 size={18} /> Buscando os aniversariantes de {MESES[mes - 1]}…
                </div>
            )}

            {!carregando && dados && !dados.filiais.length && (
                <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
                    Nenhum aniversariante em {MESES[mes - 1]}.
                </div>
            )}

            {!carregando && !!dados?.filiais?.length && imagens && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 20 }}>
                    {dados.filiais.map((f) => (
                        <CartaoFilial
                            key={f.sigla}
                            filial={f}
                            mes={dados.mes}
                            imagens={imagens}
                            aoRegistrarCanvas={registrarCanvas}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AniversariantesMes;
